/**
 * Server-side run recovery + cancellation for an AG-UI conversation.
 *
 * On mount, checks `/conversations/{id}/status`. If a run is in progress
 * (e.g. another tab kicked it off, or the user refreshed mid-stream),
 * polls until it completes, then asks the consumer to reload history.
 *
 * Also subscribes to the local agent's run lifecycle so the polling
 * state stays in sync with locally-initiated streams (a started local
 * stream supersedes any in-flight remote-status check).
 *
 * Returns `{ isRunningRemotely, isCancelling, onCancelRun }`. `onCancelRun`
 * aborts the local SSE, posts a cancel to the backend, and either
 * confirms idle (and reloads) or starts polling.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import {
  getRunningSnapshot,
  markConversationIdle,
  markConversationRunning,
} from "./running-conversations";

const POLL_INTERVAL_MS = 2000;

interface UseRemoteRunRecoveryOptions {
  agent: HttpAgent;
  api: AssistantApi;
  conversationId: string;
  clearError?: () => void;
  /**
   * Called whenever a run is detected to have finished server-side
   * (after polling completes, or after a cancel confirms idle). The
   * consumer is responsible for re-fetching and resetting the thread.
   */
  onHistoryReload: () => Promise<void>;
}

interface UseRemoteRunRecoveryReturn {
  isRunningRemotely: boolean;
  isCancelling: boolean;
  onCancelRun: () => void;
}

export function useRemoteRunRecovery({
  agent,
  api,
  conversationId,
  clearError,
  onHistoryReload,
}: UseRemoteRunRecoveryOptions): UseRemoteRunRecoveryReturn {
  const [isRunningRemotely, setIsRunningRemotely] = useState(() =>
    getRunningSnapshot().has(conversationId)
  );
  const [isCancelling, setIsCancelling] = useState(false);

  const abortPollingRef = useRef<(() => void) | null>(null);

  const pollUntilReady = useCallback(
    (cid: string) => {
      abortPollingRef.current?.();
      let aborted = false;
      abortPollingRef.current = () => {
        aborted = true;
      };

      setIsRunningRemotely(true);
      markConversationRunning(cid);

      (async () => {
        while (!aborted) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          if (aborted) return;

          try {
            const resp = await api.getRunStatus(cid);
            if (aborted) return;

            if (!resp.is_running) {
              setIsRunningRemotely(false);
              setIsCancelling(false);
              markConversationIdle(cid);
              await onHistoryReload();
              return;
            }
          } catch (error) {
            console.error("Failed to poll run status:", error);
          }
        }
      })();
    },
    [api, onHistoryReload]
  );

  // Mount-time status check. One call per conversation mount; the
  // alternative (gating on the module-level running snapshot) breaks
  // after page refresh.
  useEffect(() => {
    abortPollingRef.current?.();
    let cancelled = false;
    abortPollingRef.current = () => {
      cancelled = true;
    };

    (async () => {
      try {
        const resp = await api.getRunStatus(conversationId);
        if (cancelled) return;
        if (resp.is_running) {
          pollUntilReady(conversationId);
        } else {
          markConversationIdle(conversationId);
          setIsRunningRemotely(false);
          await onHistoryReload();
        }
      } catch (error) {
        console.error("Failed to fetch run status:", error);
      }
    })();

    return () => {
      cancelled = true;
      abortPollingRef.current?.();
      abortPollingRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // Local-stream lifecycle — supersedes remote detection.
  useEffect(() => {
    const cid = conversationId;
    const sub = agent.subscribe({
      onRunStartedEvent: () => {
        setIsRunningRemotely(false);
        abortPollingRef.current?.();
        markConversationRunning(cid);
      },
      onRunFinishedEvent: () => {
        markConversationIdle(cid);
      },
      onRunFailed: () => {
        markConversationIdle(cid);
      },
    });

    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const onCancelRun = useCallback(() => {
    clearError?.();
    agent.abortRun();
    setIsCancelling(true);

    api.cancelMessage(conversationId).then(
      async () => {
        try {
          const resp = await api.getRunStatus(conversationId);
          if (!resp.is_running) {
            setIsCancelling(false);
            setIsRunningRemotely(false);
            markConversationIdle(conversationId);
            await onHistoryReload();
          } else {
            pollUntilReady(conversationId);
          }
        } catch {
          setIsCancelling(false);
          setIsRunningRemotely(false);
          markConversationIdle(conversationId);
        }
      },
      (error) => {
        console.error("Failed to cancel run:", error);
        setIsCancelling(false);
        setIsRunningRemotely(false);
        markConversationIdle(conversationId);
      }
    );
  }, [api, agent, conversationId, clearError, onHistoryReload, pollUntilReady]);

  return { isRunningRemotely, isCancelling, onCancelRun };
}
